import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Clapperboard, ExternalLink, Play, Plus, RefreshCw, Search, Send, X } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { isEpisodeCard, groupEpisodes, flattenEntries } from '../lib/longformGroups';
import { cardTitle } from '../lib/cardTitle';
import Avatar from '../ui/Avatar';
import Button from '../ui/Button';
import CalendarCell from '../ui/CalendarCell';
import ExpandedRowPanel from '../ui/ExpandedRowPanel';
import Grid from '../ui/Grid';
import Icon from '../ui/Icon';
import Input from '../ui/Input';
import ListRow from '../ui/ListRow';
import Popover from '../ui/Popover';
import Select from '../ui/Select';
import StageChip from '../ui/StageChip';
import Badge from '../ui/Badge';
import Tag from '../ui/Tag';
import Tabs from '../ui/Tabs';
import Textarea from '../ui/Textarea';
import ErrorState from '../ui/ErrorState';
import Skeleton from '../ui/Skeleton';
import { useToast } from '../ui/Toast';
import { usePageHeader } from '../shell/pageHeader';
import { clean } from '../shell/copy';

// Edit queue on src/ui (DESIGN rev 2.1 §3.1: a status board, Grid at high density, grouped by stage).
// Same calls as before: GET /api/live-production/edit-calendar, GET /api/team-members; PATCH a card's edit notes
// or edit date, PATCH /:id/status for Start editing (Filmed to Editing) and Send for qual (Editing to Qual).
// Rows are production cards (one per record id); longform episodes stay separate rows next to each other (D-120).

const STATUS_ORDER = ['Filmed', 'Editing', 'Qual'];
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const CAL_MAX_CHIPS = 3;
const VIEWS = [
  { id: 'queue', label: 'Queue' },
  { id: 'editor', label: 'By editor' },
  { id: 'project', label: 'By project' },
  { id: 'calendar', label: 'Calendar' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

const pad = n => String(n).padStart(2, '0');
const dateKey = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

function shortDate(str) {
  if (!str) return '';
  const [y, m, d] = str.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
function longDate(key) {
  return new Date(`${key}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

/** Row title: an episode reads "Series, Ep N: episode title"; anything else its script name, notes or (Riff). */
function titleOf(r) {
  if (isEpisodeCard(r)) return `${clean(r.script?.scriptName || '(series)')}, Ep ${r.episodeNumber}${r.episodeTitle ? `: ${clean(r.episodeTitle)}` : ''}`;
  return clean(r.script?.scriptName || r.notes || '(Riff)');
}
/** Calendar chip title, as before (cardTitle output, its middle dot normalised by clean()). */
const chipTitle = r => clean(cardTitle(r));

/** The old page's order inside a group: by status (Filmed, Editing, Qual), API order (updatedAt asc) within a
 *  status, and the episodes of one series pulled together where the first one sat (groupEpisodes, D-120). */
const orderWithin = rs => flattenEntries(groupEpisodes(STATUS_ORDER.flatMap(s => rs.filter(r => r.status === s))));

// A cut in Qual is waiting on a reviewer: the §2.1 "Needs review" condition. Filmed and Editing carry none.
const conditionOf = r => (r.status === 'Qual' ? 'Needs review' : null);

function buildMonthDays(year, month) {
  const first = new Date(year, month, 1);
  const start = new Date(first); start.setDate(1 - first.getDay());
  const last = new Date(year, month + 1, 0);
  const count = Math.ceil((first.getDay() + last.getDate()) / 7) * 7;
  return Array.from({ length: count }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
}
function buildWeekDays(anchor) {
  const s = new Date(anchor); s.setDate(s.getDate() - s.getDay());
  return Array.from({ length: 7 }, (_, i) => { const d = new Date(s); d.setDate(s.getDate() + i); return d; });
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function EditQueue() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();

  const [records,      setRecords]      = useState([]);
  const [members,      setMembers]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [loadError,    setLoadError]    = useState(false);
  const [view,         setView]         = useState('queue');
  const [editorFilter, setEditorFilter] = useState('all');
  const [stageFilter,  setStageFilter]  = useState('all');
  const [search,       setSearch]       = useState('');
  const [selectedId,   setSelectedId]   = useState(null);
  const [busyId,       setBusyId]       = useState(null);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await api.get('/api/live-production/edit-calendar');
      setRecords(res.data);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);
  useEffect(() => {
    api.get('/api/team-members').then(r => setMembers(r.data)).catch(() => {});
  }, []);

  const editors = useMemo(() => members.filter(m => m.roles?.some(r => r.role === 'Editor')), [members]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return records.filter(r =>
      (editorFilter === 'all' || r.editorId === editorFilter) &&
      (stageFilter === 'all' || r.status === stageFilter) &&
      (!q || [cardTitle(r), r.episodeTitle, r.client?.name, r.editor?.name].some(s => s?.toLowerCase().includes(q))));
  }, [records, editorFilter, stageFilter, search]);
  const filtersOn = editorFilter !== 'all' || stageFilter !== 'all' || search.trim() !== '';
  const clearFilters = () => { setEditorFilter('all'); setStageFilter('all'); setSearch(''); };

  // ── Record updates (same merges as before) ──────────────────────────────────

  function handleRecordUpdated(updated) {
    setRecords(prev => prev.map(r => (r.id === updated.id ? { ...r, ...updated } : r)));
  }

  async function changeStatus(record, status) {
    setBusyId(record.id);
    try {
      const res = await api.patch(`/api/live-production/${record.id}/status`, { status, changedBy: user?.id });
      handleRecordUpdated(res.data);
      setRecords(prev => prev.map(r => (r.id === record.id ? { ...r, status } : r)));
      toast(status === 'Qual' ? `${titleOf(record)} sent for qual.` : `${titleOf(record)} moved to Editing.`);
    } catch (err) {
      toast(clean(err.response?.data?.error) || 'The stage did not change. Try again.', { tone: 'danger' });
    } finally {
      setBusyId(null);
    }
  }

  async function saveNotes(record, notes) {
    try {
      const res = await api.patch(`/api/live-production/${record.id}`, { editNotes: notes || null });
      handleRecordUpdated(res.data);
      return true;
    } catch {
      toast('The edit notes did not save. Try again.', { tone: 'danger' });
      return false;
    }
  }

  async function handleEditDateChange(record, dateStr) {
    // Optimistic: the chip moves at once, and moves back if the save fails.
    setRecords(prev => prev.map(r => (r.id === record.id ? { ...r, editScheduledDate: dateStr || null } : r)));
    try {
      const res = await api.patch(`/api/live-production/${record.id}`, { editScheduledDate: dateStr || null });
      handleRecordUpdated(res.data);
    } catch {
      setRecords(prev => prev.map(r => (r.id === record.id ? { ...r, editScheduledDate: record.editScheduledDate ?? null } : r)));
      toast('The edit date did not save. Try again.', { tone: 'danger' });
    }
  }

  // P-1: the production card is a page.
  const openProduction = useCallback(record => navigate(`/production/${record.id}`), [navigate]);

  // ── Header ──────────────────────────────────────────────────────────────────

  usePageHeader({
    title: 'Edit queue',
    meta: loading ? 'Loading' : `${plural(filtered.length, 'video')} in the edit workflow`,
    actions: <Button variant="quiet" icon={RefreshCw} onClick={fetchRecords}>Refresh</Button>,
    filters: (
      <>
        <Select aria-label="Editor" value={editorFilter} onChange={e => setEditorFilter(e.target.value)}
          options={[{ value: 'all', label: 'All editors' }, ...editors.map(m => ({ value: m.id, label: clean(m.name) }))]} />
        <span className="ui-seg" role="group" aria-label="Stage">
          {['all', ...STATUS_ORDER].map(s => (
            <Button key={s} variant="quiet" aria-pressed={stageFilter === s} onClick={() => setStageFilter(s)}>{s === 'all' ? 'All stages' : s}</Button>
          ))}
        </span>
        <Input icon={Search} aria-label="Search by title, client or editor" placeholder="Search by title, client or editor"
          value={search} onChange={e => setSearch(e.target.value)} />
        {filtersOn && <Button variant="quiet" icon={X} onClick={clearFilters}>Clear</Button>}
      </>
    ),
  });

  // ── Grid groupings ──────────────────────────────────────────────────────────

  const { groups, groupOf, rows } = useMemo(() => {
    let defs; let keyOf;
    if (view === 'editor') {
      keyOf = r => r.editorId || '__unassigned';
      const names = new Map();
      filtered.forEach(r => names.set(keyOf(r), r.editor?.name || 'Unassigned'));
      // Editors alphabetical, Unassigned last (as before).
      defs = [...names.entries()].sort(([ka, a], [kb, b]) => (ka === '__unassigned') - (kb === '__unassigned') || a.localeCompare(b))
        .map(([key, name]) => ({ key, header: <><Avatar name={clean(name)} />{clean(name)}</> }));
    } else if (view === 'project') {
      keyOf = r => r.clientId || '__unknown';
      const names = new Map();
      filtered.forEach(r => names.set(keyOf(r), r.client?.name || 'Unknown project'));
      defs = [...names.entries()].sort(([, a], [, b]) => a.localeCompare(b)).map(([key, name]) => ({ key, header: clean(name) }));
    } else {
      keyOf = r => r.status;
      defs = STATUS_ORDER.map(s => ({ key: s, header: <StageChip stage={s} /> }));
    }
    const ordered = defs.flatMap(g => orderWithin(filtered.filter(r => keyOf(r) === g.key)));
    return { groups: defs, groupOf: keyOf, rows: ordered };
  }, [view, filtered]);

  // Series progress for the side panel (the old series sub-header: "N episodes, 2/5 Posted, next Ep 3: Edit").
  const seriesById = useMemo(() => {
    const map = new Map();
    groupEpisodes(STATUS_ORDER.flatMap(s => filtered.filter(r => r.status === s)))
      .filter(e => e.kind === 'series').forEach(e => e.episodes.forEach(ep => map.set(ep.id, e)));
    return map;
  }, [filtered]);

  const columns = [
    { key: 'title', label: 'Title', width: 300, render: r => titleOf(r) },
    { key: 'stage', label: 'Stage', width: 124, render: r => <StageChip stage={r.status} /> },
    { key: 'condition', label: 'Condition', width: 132, render: r => (conditionOf(r) ? <Badge condition={conditionOf(r)} /> : null) },
    { key: 'client', label: 'Client', width: 160, render: r => (r.client?.name ? <Tag>{clean(r.client.name)}</Tag> : <span className="ui-meta">No client</span>) },
    { key: 'editor', label: 'Editor', width: 140, render: r => (r.editor?.name ? clean(r.editor.name) : <span className="ui-meta">Unassigned</span>) },
    { key: 'edit', label: 'Edit scheduled', width: 112, render: r => (r.editScheduledDate ? shortDate(r.editScheduledDate) : <span className="ui-meta">Not set</span>) },
    { key: 'notes', label: 'Edit notes', width: 220, render: r => (r.editNotes ? clean(r.editNotes) : null) },
    { key: 'updated', label: 'Updated', width: 88, render: r => shortDate(r.updatedAt) },
  ];

  const rowActions = r => (
    <>
      {r.status === 'Filmed' && <Button variant="quiet" iconOnly icon={Play} label={`Start editing ${titleOf(r)}`} loading={busyId === r.id} onClick={() => changeStatus(r, 'Editing')} />}
      {r.status === 'Editing' && <Button variant="quiet" iconOnly icon={Send} label={`Send ${titleOf(r)} for qual`} loading={busyId === r.id} onClick={() => changeStatus(r, 'Qual')} />}
      {r.frameLink && (
        <a className="ui ui-btn ui-btn--quiet ui-btn--icon" href={r.frameLink} target="_blank" rel="noreferrer" aria-label={`Open the Frame.io review of ${titleOf(r)}`} title="Frame.io review">
          <Icon icon={ExternalLink} />
        </a>
      )}
      <Button variant="quiet" iconOnly icon={Clapperboard} label={`Open production ${titleOf(r)}`} onClick={() => openProduction(r)} />
    </>
  );

  const selected = selectedId ? records.find(r => r.id === selectedId) : null;
  const isGrid = view !== 'calendar';

  return (
    <div className="ui ui-page ui-page--flush">
      <div className="ui-inset">
        <Tabs label="Edit queue view" value={view} onChange={setView} items={VIEWS} />
      </div>

      {isGrid && (
        <div className="ui-work">
          <Grid
            label={`Edit queue, ${VIEWS.find(v => v.id === view).label.toLowerCase()}`}
            columns={columns} rows={rows} rowKey={r => r.id} groups={groups} groupOf={groupOf}
            density="high" storageKey="hf-grid:edit-queue" selectedKey={selectedId}
            onOpen={r => setSelectedId(r.id)} rowActions={rowActions}
            state={loadError ? 'error' : loading ? 'loading' : 'data'}
            empty={filtersOn
              ? { message: 'No video matches these filters. Clear them to see the whole queue.', action: { label: 'Clear filters', icon: X, onClick: clearFilters } }
              : { message: 'No video is in Filmed, Editing or Qual. Mark a shoot filmed on its production page and it shows up here.' }}
            error={{ title: 'The edit queue did not load', message: 'Check your connection, then try again.', onRetry: fetchRecords }} />
          <ExpandedRowPanel open={!!selected} title={selected ? titleOf(selected) : ''} onClose={() => setSelectedId(null)} label="Video details"
            footer={selected && (
              <>
                {selected.status === 'Filmed' && <Button variant="primary" icon={Play} loading={busyId === selected.id} onClick={() => changeStatus(selected, 'Editing')}>Start editing</Button>}
                {selected.status === 'Editing' && <Button variant="primary" icon={Send} loading={busyId === selected.id} onClick={() => changeStatus(selected, 'Qual')}>Send for qual</Button>}
                <Button variant="secondary" icon={Clapperboard} onClick={() => openProduction(selected)}>Open production</Button>
              </>
            )}>
            {selected && <RecordDetails key={selected.id} record={selected} series={seriesById.get(selected.id)} onSaveNotes={saveNotes} />}
          </ExpandedRowPanel>
        </div>
      )}

      {!isGrid && (
        <div className="ui-pane">
          {loadError && <ErrorState title="The edit queue did not load" message="Check your connection, then try again." onRetry={fetchRecords} />}
          {!loadError && loading && <div className="ui-region ui-region--pad ui-stack"><Skeleton width="25%" /><Skeleton width="60%" /><Skeleton width="50%" /></div>}
          {!loadError && !loading && <EditCalendar records={filtered} onDateChange={handleEditDateChange} onOpen={openProduction} />}
        </div>
      )}
    </div>
  );
}

// ── Side panel body: the old record card's fields, edit notes and the Frame.io link ──

function RecordDetails({ record, series, onSaveNotes }) {
  const [notes,  setNotes]  = useState(record.editNotes || '');
  const [dirty,  setDirty]  = useState(false);
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!dirty) return;
    setSaving(true);
    const ok = await onSaveNotes(record, notes);
    if (ok) setDirty(false);
    setSaving(false);
  }

  const condition = conditionOf(record);
  return (
    <div className="ui-stack">
      <dl className="ui-kv">
        <dt>Stage</dt><dd><StageChip stage={record.status} /></dd>
        {condition && <><dt>Condition</dt><dd><Badge condition={condition} /></dd></>}
        <dt>Client</dt><dd>{record.client?.name ? clean(record.client.name) : 'No client'}</dd>
        <dt>Editor</dt><dd>{record.editor?.name ? clean(record.editor.name) : 'Unassigned'}</dd>
        <dt>Edit scheduled</dt><dd>{record.editScheduledDate ? shortDate(record.editScheduledDate) : 'Not set'}</dd>
        {series && <><dt>Series</dt><dd>{clean(series.scriptName)}: {plural(series.episodes.length, 'episode')}, {clean(series.progress.label)}</dd></>}
        <dt>Updated</dt><dd>{shortDate(record.updatedAt)}</dd>
      </dl>
      <Textarea label="Edit notes" placeholder="Handoff notes for the editor" rows={5} value={notes}
        onChange={e => { setNotes(e.target.value); setDirty(true); }} onBlur={save} />
      {dirty && <span><Button variant="secondary" loading={saving} onClick={save}>Save notes</Button></span>}
      {record.frameLink && (
        <span>
          <a className="ui ui-btn ui-btn--quiet" href={record.frameLink} target="_blank" rel="noreferrer">
            <Icon icon={ExternalLink} />Open Frame.io review
          </a>
        </span>
      )}
    </div>
  );
}

// ── Calendar ──────────────────────────────────────────────────────────────────

// A draggable chip: the title opens the production page, × clears the edit date (scheduled chips only).
function RecordChip({ record, onOpen, onClear, suffix }) {
  const title = chipTitle(record);
  return (
    <span className="ui-chip" draggable title={`${title}${record.client?.name ? `, ${clean(record.client.name)}` : ''}`}
      onDragStart={e => { e.stopPropagation(); e.dataTransfer.setData('text/plain', record.id); e.dataTransfer.effectAllowed = 'move'; }}>
      <button type="button" className="ui-chip__main" onClick={() => onOpen(record)}>
        <span className="ui-chip__tag">{record.status}</span>
        <span className="ui-chip__title">{title}{suffix ? `, ${suffix}` : ''}</span>
      </button>
      {onClear && (
        <button type="button" className="ui-chip__x" aria-label={`Clear the edit date of ${title}`} title="Clear the edit date" onClick={() => onClear(record)}>
          <Icon icon={X} />
        </button>
      )}
    </span>
  );
}

// The day's assign picker: search the unscheduled videos (title, client or editor) and put one on this day.
function DayPicker({ date, align, unscheduled, openDay, setOpenDay, onDateChange }) {
  const [search, setSearch] = useState('');
  const open = openDay === date;
  const q = search.trim().toLowerCase();
  const matches = !q ? unscheduled : unscheduled.filter(r =>
    r.script?.scriptName?.toLowerCase().includes(q) || r.client?.name?.toLowerCase().includes(q) || r.editor?.name?.toLowerCase().includes(q));
  const label = longDate(date);
  return (
    <Popover open={open} onOpenChange={o => { setOpenDay(o ? date : null); setSearch(''); }} align={align} label={`Schedule an edit on ${label}`}
      trigger={({ toggle, ref, props }) => (
        <Button ref={ref} variant="quiet" iconOnly icon={Plus} label={`Schedule an edit on ${label}`} onClick={toggle} {...props} />
      )}>
      <div className="ui-popbody">
        <span className="ui-field__label">Schedule an edit on {label}</span>
        <Input aria-label="Search videos" placeholder="Search videos" value={search} onChange={e => setSearch(e.target.value)} autoFocus />
        <div className="ui-scroll ui-list">
          {matches.length === 0
            ? <p className="ui-p ui-dense ui-text-3">{unscheduled.length === 0 ? 'Every video in the queue already has an edit date.' : 'No unscheduled video matches that search.'}</p>
            : matches.map(r => (
              <ListRow key={r.id} leading={<StageChip stage={r.status} />} title={chipTitle(r)}
                meta={[r.client?.name, r.editor?.name].filter(Boolean).map(clean).join(', ')}
                onClick={() => { onDateChange(r, date); setOpenDay(null); }} />
            ))}
        </div>
      </div>
    </Popover>
  );
}

function EditCalendar({ records, onDateChange, onOpen }) {
  const [calView,  setCalView]  = useState('week'); // 'week' | 'month', week first as before
  const [viewDate, setViewDate] = useState(() => new Date());
  const [dragOver, setDragOver] = useState(null);
  const [openDay,  setOpenDay]  = useState(null);

  const todayKey = dateKey(new Date());
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const days = useMemo(() => (calView === 'week' ? buildWeekDays(viewDate) : buildMonthDays(year, month)), [calView, viewDate, year, month]);

  const step = dir => setViewDate(d => {
    const n = new Date(d);
    if (calView === 'week') n.setDate(n.getDate() + 7 * dir); else n.setMonth(n.getMonth() + dir);
    return n;
  });

  const navLabel = calView === 'week'
    ? `${days[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${days[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
    : viewDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const byDate = useMemo(() => {
    const map = {};
    records.forEach(r => {
      if (!r.editScheduledDate) return;
      const k = r.editScheduledDate.slice(0, 10);
      (map[k] = map[k] || []).push(r);
    });
    return map;
  }, [records]);
  const unscheduled = useMemo(() => records.filter(r => !r.editScheduledDate), [records]);

  const drop = key => e => {
    e.preventDefault(); setDragOver(null);
    const id = e.dataTransfer.getData('text/plain');
    const rec = id && records.find(r => r.id === id);
    if (rec) onDateChange(rec, key);
  };

  return (
    <div className="ui-stack">
      <div className="ui-toolbar">
        <Button variant="quiet" iconOnly icon={ChevronLeft} label={calView === 'week' ? 'Previous week' : 'Previous month'} onClick={() => step(-1)} />
        <span className="ui-heading" aria-live="polite">{navLabel}</span>
        <Button variant="quiet" iconOnly icon={ChevronRight} label={calView === 'week' ? 'Next week' : 'Next month'} onClick={() => step(1)} />
        <Button variant="quiet" onClick={() => setViewDate(new Date())}>Today</Button>
        <span className="ui-spacer" />
        <span className="ui-meta">{records.length ? 'Drag a video onto a day to set its edit date.' : 'No video matches the filters. Clear them to see the queue.'}</span>
        <span className="ui-seg" role="group" aria-label="Calendar range">
          <Button variant="quiet" aria-pressed={calView === 'week'} onClick={() => setCalView('week')}>Week</Button>
          <Button variant="quiet" aria-pressed={calView === 'month'} onClick={() => setCalView('month')}>Month</Button>
        </span>
      </div>

      <div className="ui-month">
        {DOW.map(d => <div key={d} className="ui-month__dow" aria-hidden="true">{d}</div>)}
        {days.map(day => {
          const key = dateKey(day);
          const dow = day.getDay();
          return (
            <CalendarCell key={key} date={key} today={key === todayKey} outside={calView === 'month' && day.getMonth() !== month}
              max={CAL_MAX_CHIPS} items={byDate[key] || []}
              renderItem={r => <RecordChip record={r} onOpen={onOpen} onClear={rec => onDateChange(rec, null)} />}
              action={<DayPicker date={key} align={dow >= 4 ? 'end' : 'start'} unscheduled={unscheduled} openDay={openDay} setOpenDay={setOpenDay} onDateChange={onDateChange} />}
              data-drop={dragOver === key || undefined}
              onDragOver={e => { e.preventDefault(); setDragOver(key); }}
              onDragLeave={() => setDragOver(null)}
              onDrop={drop(key)} />
          );
        })}
      </div>

      {unscheduled.length > 0 && (
        <section className="ui-section" aria-labelledby="eq-unscheduled">
          <div className="ui-section__head">
            <h2 className="ui-section__title" id="eq-unscheduled">Unscheduled</h2>
            <span className="ui-meta">{unscheduled.length}</span>
          </div>
          <div className="ui-chip-row">
            {unscheduled.map(r => <RecordChip key={r.id} record={r} onOpen={onOpen} suffix={r.client?.initials ? clean(r.client.initials) : ''} />)}
          </div>
        </section>
      )}
    </div>
  );
}
